import { useRouter } from 'next/router'
import { ErrorPage, ResponsiveContext } from 'components'
import { LayoutAndErrorBundary } from 'components/LayoutAndErrorBundary'
import { MetaHead } from '../../components'
import { ClubAhorro } from '../../components/ClubAhorro'
import { NextPageWithLayout } from 'shared/interfaces/Layout'
import { cleanPageNumber } from 'shared/helpers'
import {
  IContentfulResponse,
  IEntriesProps,
  getContentfulData
} from '@smuchile/pkg-unimarc-hooks'
import { SeoInfoProps } from 'pages/static/[...staticPage]'
import { ClubUnimarcHeader } from 'components/ClubAhorro/ClubUnimarcHeader'
import { ClubUnimarcFooter } from 'components/ClubAhorro/ClubUnimarcFooter'
import { ClubUnimarcStaticBanner } from 'shared/interfaces/ClubUnimarc'
import { MEMBERSHIP_SAVINGS } from 'shared/utils/constanst'
import { getContentfulCouponsClub } from 'shared/contentful/club'
import CurtainWrapper from 'components/CurtainWrapper'

export interface ClubAhorroPageProps {
  banner: ClubUnimarcStaticBanner
  data: IContentfulResponse
  dataCFMembershipLeves: IContentfulResponse
  dataCFSaving: IContentfulResponse
  footer: IEntriesProps
  isMobile: boolean
  seoInfo?: SeoInfoProps
  statusCode?: number
}

const ClubAhorroPage: NextPageWithLayout<ClubAhorroPageProps> = ({
  banner,
  data,
  dataCFMembershipLeves,
  dataCFSaving,
  footer,
  isMobile,
  seoInfo,
  statusCode
}: ClubAhorroPageProps) => {
  const router = useRouter()

  if (statusCode === 500) {
    return <ErrorPage />
  }

  return (
    <ResponsiveContext isMobile={isMobile}>
      <>
        <MetaHead
          canonicalPath={cleanPageNumber(router?.asPath)}
          contentDescription={seoInfo?.description}
          pageImg={seoInfo?.previewImage}
          titleName={seoInfo?.pageTitle}
          type='CU'
        />

        <ClubUnimarcHeader
          isMobile={isMobile}
          showBackArrow={false}
          showBackArrowMobile={true}
        />

        <ClubAhorro
          banner={banner}
          data={data}
          dataCFMembershipLeves={dataCFMembershipLeves}
          dataCFSaving={dataCFSaving}
          isMobile={isMobile}
        />

        <ClubUnimarcFooter data={footer} />
      </>
    </ResponsiveContext>
  )
}

ClubAhorroPage.getLayout = (page) => {
  return (
    <>
      <CurtainWrapper>
        <LayoutAndErrorBundary
          backgroundColor='--color-base-white'
          hideFooter
          hideHeader
        >
          {page}
        </LayoutAndErrorBundary>
      </CurtainWrapper>
    </>
  )
}

export async function getServerSideProps() {
  const { data, statusCode } = await getContentfulCouponsClub()

  const desktopBanner = await getContentfulData({
    options: {
      content_type: 'featureContentBanners',
      'fields.label[in]': 'Banner Club Unimarc web'
    }
  })

  const footer = await getContentfulData({
    id_contentful: 'contentful_minifooter',
    options: {
      content_type: 'unimarcWebTopFooter',
      'fields.label[in]': 'Mini Footer (Desktop)',
      'fields.platform': 'Web Unimarc eComm'
    }
  })

  const dataCFSaving = await getContentfulData({
    id_contentful: 'contentful-builtin-asset-savings',
    options: {
      'sys.id': MEMBERSHIP_SAVINGS[0]
    },
    type: 'assets'
  })

  const dataCFMembershipLeves = await getContentfulData({
    id_contentful: 'contentful_membership_levels',
    options: {
      content_type: 'featureContentBanners',
      'fields.label[match]': 'Beneficios por nivel mobile | Club Unimarc WEB',
      include: 5
    }
  })

  const seoInfo = data?.items[0]?.seoInfo ?? null

  if (!data || !dataCFMembershipLeves) {
    return {
      notFound: true
    }
  }
  return {
    props: {
      data,
      seoInfo,
      statusCode,
      banner: desktopBanner ?? null,
      footer,
      dataCFSaving,
      dataCFMembershipLeves
    }
  }
}

export default ClubAhorroPage
